import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, UploadSimple, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImportCsvDialog from "@/components/ImportCsvDialog";
import { useAuth } from "@/context/AuthContext";

export default function BankAccounts() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [importBank, setImportBank] = useState(null);
  const [form, setForm] = useState({
    name: "",
    bank: "",
    currency: "TRY",
    opening_balance: "",
    iban: "",
  });

  const { data } = useQuery({
    queryKey: ["banks-summary"],
    queryFn: async () => (await api.get("/bank-accounts/summary")).data,
  });

  const [editing, setEditing] = useState(null);
  const resetForm = () => setForm({ name: "", bank: "", currency: "TRY", opening_balance: "", iban: "" });

  const create = useMutation({
    mutationFn: async (p) => (await api.post("/bank-accounts", p)).data,
    onSuccess: () => {
      qc.invalidateQueries(); setOpen(false); setEditing(null); resetForm();
      toast.success("Banka hesabı eklendi");
    },
  });
  const update = useMutation({
    mutationFn: async ({ id, p }) => (await api.put(`/bank-accounts/${id}`, p)).data,
    onSuccess: () => {
      qc.invalidateQueries(); setOpen(false); setEditing(null); resetForm();
      toast.success("Banka hesabı güncellendi");
    },
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/bank-accounts/${id}`)).data,
    onSuccess: () => qc.invalidateQueries(),
  });

  const startEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || "", bank: a.bank || "", currency: a.currency || "TRY",
      opening_balance: String(a.opening_balance ?? ""), iban: a.iban || "",
    });
    setOpen(true);
  };
  const startCreate = () => { setEditing(null); resetForm(); setOpen(true); };

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form, opening_balance: parseFloat(form.opening_balance || 0) };
    if (editing) update.mutate({ id: editing.id, p: payload });
    else create.mutate(payload);
  };

  const accounts = data?.accounts || [];
  const total = data?.total_try || 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="bank-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Nakit Konumu</div>
          <h1 className="mt-1 text-3xl font-black tracking-tight">Banka Hesapları</h1>
        </div>
        {isAdmin && (
          <Button data-testid="bank-add-btn" onClick={startCreate}>
            <Plus size={16} className="mr-1" /> Yeni Hesap
          </Button>
        )}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Banka Hesabı Düzenle" : "Yeni Banka Hesabı"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Hesap Adı</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bank-name" />
              </div>
              <div>
                <Label>Banka</Label>
                <Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Para Birimi</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRY">TRY</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Açılış Bakiyesi</Label>
                  <Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>IBAN</Label>
                <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="bank-save">{editing ? "Güncelle" : "Kaydet"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 rounded-md border border-border bg-white p-6">
        <div className="overline">Toplam Nakit</div>
        <div className="mono mt-2 text-4xl font-semibold text-primary">{fmtMoney(total)}</div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {accounts.map((a) => (
          <div key={a.id} className="rounded-md border border-border bg-white p-6" data-testid={`bank-${a.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">{a.bank || "Banka"}</div>
                <div className="mt-1 text-lg font-semibold">{a.name}</div>
              </div>
              {isAdmin && <div className="flex items-center gap-1">
                <button
                  onClick={() => setImportBank(a)}
                  data-testid={`import-bank-${a.id}`}
                  className="rounded border border-border p-1.5 text-foreground hover:bg-secondary"
                  title="CSV içe aktar"
                >
                  <UploadSimple size={14} />
                </button>
                <button
                  onClick={() => startEdit(a)}
                  data-testid={`edit-bank-${a.id}`}
                  className="rounded border border-border p-1.5 text-foreground hover:bg-secondary"
                  title="Düzenle"
                >
                  <PencilSimple size={14} />
                </button>
                <button
                  onClick={() => remove.mutate(a.id)}
                  data-testid={`del-bank-${a.id}`}
                  className="rounded border border-border p-1.5 text-destructive hover:bg-destructive/5"
                >
                  <Trash size={14} />
                </button>
              </div>}
            </div>
            <div className="mt-4">
              <div className="mono text-2xl font-semibold">
                {fmtMoney(a.balance, a.currency)}
              </div>
              {a.currency !== "TRY" && (
                <div className="mono mt-1 text-xs text-muted-foreground">
                  ≈ {fmtMoney(a.balance_try, "TRY")}
                </div>
              )}
            </div>
            {a.iban && (
              <div className="mono mt-3 truncate text-xs text-muted-foreground">{a.iban}</div>
            )}
          </div>
        ))}
        {accounts.length === 0 && (
          <div className="col-span-full py-12 text-center text-sm text-muted-foreground">
            Henüz banka hesabı yok. Yeni hesap ekleyin.
          </div>
        )}
      </div>
      {importBank && (
        <ImportCsvDialog
          open={!!importBank}
          onOpenChange={(v) => !v && setImportBank(null)}
          bank={importBank}
        />
      )}
    </div>
  );
}
